import * as echarts from 'echarts/core'
import { BarChart, FunnelChart, GaugeChart, LineChart, PieChart } from 'echarts/charts'
import { GridComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { isEnglish } from '../../lib/i18n'
import { strings as S } from './strings'

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  /* The report reads as a report because the numbers are shapes: a dial for a rate, a funnel
     for a population narrowing, a ranked bar for a league table. */
  GaugeChart,
  FunnelChart,
  GridComponent,
  LegendComponent,
  CanvasRenderer,
])

/*
 * The charts as they appear IN THE DOCUMENT, drawn to images before the page is assembled.
 *
 * They are built here rather than reused from the board for two reasons. The board's options
 * resolve their colours from the live theme, so exporting while the reader had dark mode on
 * would have put a dark chart on white paper. And a chart sized for a 1500px screen does not
 * simply shrink to 170mm — the labels collide, the legend wraps into the plot and the value
 * annotations turn into a smear. These are drawn at document scale with a fixed palette.
 *
 * The palette is the security-operations one: deep slate for structure, emerald for good news,
 * amber for attention and red for failure — the same meanings the platform uses everywhere,
 * with the contrast a printed page needs rather than the contrast a backlit screen gets away
 * with.
 */

export const INK = {
  ink: '#0f172a',
  muted: '#64748b',
  grid: '#e6ebf2',
  axis: '#94a3b8',
  success: '#0f8a6d',
  failure: '#c0392f',
  incomplete: '#d98324',
  absent: '#64748b',
  accent: '#0e7a63',
  info: '#0e6d9c',
}

const BASE = {
  animation: false,
  backgroundColor: 'transparent',
  textStyle: { fontFamily: 'Tajawal, "Segoe UI", system-ui, sans-serif', color: INK.ink },
}

const legend = {
  bottom: 0,
  icon: 'roundRect',
  itemWidth: 10,
  itemHeight: 10,
  itemGap: 18,
  textStyle: { fontSize: 12, color: INK.muted },
}

const axisLabel = { fontSize: 11, color: INK.muted, hideOverlap: true }

const valueAxis = {
  type: 'value',
  splitLine: { lineStyle: { color: INK.grid } },
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel,
}

const categoryAxis = (data) => ({
  type: 'category',
  data,
  axisLine: { lineStyle: { color: INK.grid } },
  axisTick: { show: false },
  axisLabel: {
    ...axisLabel,
    /* A programme name is a sentence; at document width only the head of it fits, and a
       truncated label beats four rotated ones overlapping. */
    formatter: (value) => (String(value).length > 22 ? `${String(value).slice(0, 21)}…` : value),
  },
})

/** The four outcomes per programme, grouped — the report's headline chart. */
export const documentProgramsOption = (rows) => ({
  ...BASE,
  legend,
  grid: { top: 24, right: 16, bottom: 52, left: 44 },
  xAxis: categoryAxis(rows.map((row) => row.name)),
  yAxis: valueAxis,
  series: [
    ['success', INK.success],
    ['failure', INK.failure],
    ['incomplete', INK.incomplete],
    ['absent', INK.absent],
  ].map(([key, color]) => ({
    name: S.series[key],
    type: 'bar',
    data: rows.map((row) => row[key]),
    barMaxWidth: 26,
    itemStyle: { color, borderRadius: [3, 3, 0, 0] },
  })),
})

/** Passes against failures over time. */
export const documentLineOption = (series) => ({
  ...BASE,
  legend,
  grid: { top: 24, right: 16, bottom: 52, left: 44 },
  xAxis: { ...categoryAxis(series.labels), boundaryGap: false },
  yAxis: valueAxis,
  series: [
    [S.series.passed, series.success, INK.success],
    [S.series.failed, series.failure, INK.failure],
  ].map(([name, data, color]) => ({
    name,
    type: 'line',
    data,
    smooth: 0.3,
    symbolSize: 5,
    lineStyle: { width: 2, color },
    itemStyle: { color },
    areaStyle: {
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: `${color}26` },
          { offset: 1, color: `${color}00` },
        ],
      },
    },
  })),
})

/** The assessments as a pair of columns each — distinct results, not a series (plan F-21). */
export const documentAssessmentOption = (series) => ({
  ...BASE,
  legend,
  grid: { top: 24, right: 16, bottom: 52, left: 44 },
  xAxis: categoryAxis(series.labels),
  yAxis: { ...valueAxis, minInterval: 1 },
  series: [
    [S.series.passed, series.success, INK.success],
    [S.series.failed, series.failure, INK.failure],
  ].map(([name, data, color]) => ({
    name,
    type: 'bar',
    data,
    barMaxWidth: 22,
    itemStyle: { color, borderRadius: [3, 3, 0, 0] },
    label: { show: true, position: 'top', fontSize: 10, color: INK.ink, formatter: ({ value }) => (value ? value : '') },
  })),
})

/**
 * The Reports summary's campaign trend, for print: click and report rate per campaign in launch
 * order, as straight segments between points — the line between two campaigns is not a
 * measurement, so it is never smoothed. Marker shape tells the two apart as well as colour.
 *
 * @param {{ label: string, click: number|null, report: number|null }[]} points
 */
export const documentCampaignTrendOption = (points, { click, report }) => ({
  ...BASE,
  legend: { ...legend, data: [click, report] },
  grid: { top: 24, right: 16, bottom: 52, left: 44 },
  xAxis: { ...categoryAxis(points.map((point) => point.label)), boundaryGap: points.length < 2 },
  yAxis: { ...valueAxis, min: 0, max: 100, axisLabel: { ...valueAxis.axisLabel, formatter: (value) => `${value}%` } },
  series: [
    [click, 'click', INK.failure, 'circle'],
    [report, 'report', INK.success, 'diamond'],
  ].map(([name, key, color, symbol]) => ({
    name,
    type: 'line',
    smooth: false,
    connectNulls: false,
    symbol,
    symbolSize: symbol === 'diamond' ? 10 : 8,
    lineStyle: { width: 2, color },
    itemStyle: { color },
    label: { show: points.length <= 12, position: 'top', fontSize: 10, color: INK.ink, formatter: ({ value }) => (value == null ? '' : `${value}%`) },
    data: points.map((point) => point[key]),
  })),
})

/** The outcome split, as a ring with the dominant share named in the middle. */
export const documentDonutOption = (shares) => {
  const data = [
    { name: S.shares.success, value: shares.success ?? 0, itemStyle: { color: INK.success } },
    { name: S.shares.failure, value: shares.failure ?? 0, itemStyle: { color: INK.failure } },
    { name: S.shares.incomplete, value: shares.incomplete ?? 0, itemStyle: { color: INK.incomplete } },
    { name: S.shares.absent, value: shares.absent ?? 0, itemStyle: { color: INK.absent } },
  ]

  return {
    ...BASE,
    legend: { ...legend, bottom: 0 },
    series: [
      {
        type: 'pie',
        radius: ['54%', '78%'],
        center: ['50%', '43%'],
        data,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
      },
    ],
  }
}

/**
 * An option drawn to a PNG the document can place as an image.
 *
 * Off-DOM: the chart is built into a detached element, read, and disposed. Nothing is ever
 * attached to the page, so exporting does not make the screen flicker.
 *
 * `pixelRatio: 3` is what keeps the result sharp — a chart placed 170mm wide on A4 is being
 * asked for roughly 200dpi, and a 1x canvas at that size prints visibly soft.
 */
export function chartToImage(option, { width = 900, height = 340, pixelRatio = 3 } = {}) {
  const host = document.createElement('div')
  host.style.cssText = `width:${width}px;height:${height}px;position:absolute;left:-10000px;top:0`
  document.body.appendChild(host)

  const chart = echarts.init(host, null, { renderer: 'canvas', devicePixelRatio: pixelRatio })
  try {
    chart.setOption({ ...option, textStyle: { ...BASE.textStyle, ...(option.textStyle ?? {}) } })
    return chart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#ffffff' })
  } finally {
    chart.dispose()
    host.remove()
  }
}

/** Latin digits in both builds — a report is a document of record, not a reading surface. */
export const documentLocale = isEnglish ? 'en-GB' : 'ar-u-ca-gregory-nu-latn'

/**
 * A rate as a dial — the shape an executive summary uses for "how are we doing".
 *
 * ONE COLOUR, NOT A VERDICT (plan F-14). The arc used to be green, amber or red at thresholds
 * nobody had defined or written down, so a printed page told an executive "red" with no rule
 * behind it. Until an organisation sets its own targets, the dial says how far, not how good.
 */
export const documentGaugeOption = (value) => {
  const colour = value == null ? INK.absent : INK.info

  return {
    ...BASE,
    series: [
      {
        type: 'gauge',
        startAngle: 210,
        endAngle: -30,
        min: 0,
        max: 100,
        radius: '86%',
        center: ['50%', '58%'],
        progress: { show: true, width: 13, roundCap: true, itemStyle: { color: colour } },
        axisLine: { lineStyle: { width: 13, color: [[1, '#eef2f6']] } },
        pointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        /* NO TITLE IN THE CHART. A three-word Arabic indicator name wrapped to three lines of
           8px inside the canvas and collided with the arc; set as HTML underneath it is the
           document's own type at the document's own size. */
        title: { show: false },
        detail: {
          offsetCenter: [0, '4%'],
          fontSize: 30,
          fontWeight: 800,
          color: INK.ink,
          formatter: (v) => `${Math.round(v * 10) / 10}%`,
        },
        data: [{ value: value ?? 0 }],
      },
    ],
  }
}

/**
 * The participation funnel: one population narrowing through the stages of a programme.
 *
 * Every stage is a subset of the one before it, which is the whole reason it is a funnel and
 * not four bars — the shape carries the drop-off that the numbers alone make you subtract.
 */
export const documentFunnelOption = (stages) => ({
  ...BASE,
  series: [
    {
      type: 'funnel',
      top: 10,
      bottom: 10,
      left: '6%',
      right: '6%',
      minSize: '28%',
      gap: 3,
      sort: 'none',
      label: {
        position: 'inside',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
        formatter: ({ name, value }) => `${name}  ·  ${value}`,
      },
      labelLine: { show: false },
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      data: stages.map((stage, index) => ({
        name: stage.label,
        value: stage.value,
        itemStyle: { color: FUNNEL_INKS[index] ?? INK.absent },
      })),
    },
  ],
})

const FUNNEL_INKS = ['#0b3b31', '#0e7a63', '#1aa17f', '#4cc3a2']

/**
 * Per-campaign outcomes as grouped columns, for print. The colour ramp is the severity: opened
 * is neutral, clicked attention, submitted loss, reported the win.
 *
 * @param {{ name: string, opened: number, clicked: number, submitted: number, reported: number }[]} rows
 * @param {{ opened: string, clicked: string, submitted: string, reported: string }} labels
 */
export const documentCampaignBarsOption = (rows, labels) => ({
  ...BASE,
  legend: { ...legend, top: 0, bottom: undefined },
  grid: { top: 40, right: 16, bottom: 64, left: 44 },
  xAxis: {
    ...categoryAxis(rows.map((row) => row.name)),
    axisLabel: { ...categoryAxis([]).axisLabel, interval: 0, hideOverlap: false, width: 90, overflow: 'truncate' },
  },
  yAxis: { ...valueAxis, minInterval: 1 },
  series: [
    ['opened', INK.absent],
    ['clicked', INK.incomplete],
    ['submitted', INK.failure],
    ['reported', INK.success],
  ].map(([key, color]) => ({
    name: labels[key],
    type: 'bar',
    barMaxWidth: 16,
    itemStyle: { color, borderRadius: [3, 3, 0, 0] },
    data: rows.map((row) => Number(row[key]) || 0),
  })),
})

/**
 * A league table drawn as bars: the part achieved against the whole, one row per department or
 * path, longest first. The remainder is drawn in a pale track so a short bar reads as "few of
 * many" rather than merely as a small number.
 */
export const documentRankedOption = (rows, { doneLabel, restLabel, limit = 6 } = {}) => {
  const top = rows.slice(0, limit)
  /* `name` and `remainder` — the shape `ranking()` in the service builds. Reading `label` gave
     every row of every league table the axis label `undefined`. */
  const names = top.map((row) => row.name).reverse()
  const done = top.map((row) => row.value).reverse()
  const rest = top.map((row) => row.remainder ?? Math.max(0, (row.total ?? 0) - (row.value ?? 0))).reverse()

  return {
    ...BASE,
    legend: { ...legend, bottom: 0 },
    grid: { top: 8, right: 20, bottom: 40, left: 8, containLabel: true },
    xAxis: { ...valueAxis, splitLine: { lineStyle: { color: INK.grid } } },
    yAxis: {
      type: 'category',
      data: names,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...axisLabel,
        fontSize: 11,
        formatter: (value) => (String(value).length > 26 ? `${String(value).slice(0, 25)}…` : value),
      },
    },
    series: [
      {
        name: doneLabel,
        type: 'bar',
        stack: 'total',
        data: done,
        barMaxWidth: 16,
        itemStyle: { color: INK.accent, borderRadius: [0, 0, 0, 0] },
        label: { show: true, position: 'insideRight', color: '#fff', fontSize: 10, fontWeight: 700 },
      },
      {
        name: restLabel,
        type: 'bar',
        stack: 'total',
        data: rest,
        barMaxWidth: 16,
        itemStyle: { color: '#e8edf3' },
      },
    ],
  }
}

/** The platform's content, as bars rather than five boxes with five numbers in them. */
export const documentContentOption = (items) => ({
  ...BASE,
  grid: { top: 10, right: 28, bottom: 8, left: 8, containLabel: true },
  xAxis: { ...valueAxis, axisLabel: { show: false }, splitLine: { show: false } },
  yAxis: {
    type: 'category',
    data: items.map((item) => item.label).reverse(),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { ...axisLabel, fontSize: 12 },
  },
  series: [
    {
      type: 'bar',
      data: items.map((item) => item.value).reverse(),
      barMaxWidth: 18,
      itemStyle: { color: INK.accent, borderRadius: [0, 3, 3, 0] },
      label: { show: true, position: 'right', fontSize: 12, fontWeight: 800, color: INK.ink },
    },
  ],
})
